import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@nexus/fff-ui-shared/Button';
import Modal from '@nexus/fff-ui-shared/Modal';
import CloseButton from '@nexus/fff-ui-shared/CloseButton';
import { showSuccessToast, showErrorToast } from '@nexus/fff-ui-shared/Toast';

import { createCategory } from 'services/api/admin-categories';

import './CategoryModal.css';

const CategoryModal = ({
  isOpen,
  categories,
  onClickClose,
  getAllCategories,
}) => {
  const defaultParentCategoryName = 'Select Category';
  const defaultCategoryData = {
    name: '',
    parentCategoryId: undefined,
  };

  const [categoryData, setCategoryData] = useState(defaultCategoryData);
  const [activeSelect, setActiveSelect] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [
    selectedParentCategoryName,
    setSelectedParentCategoryName,
  ] = React.useState(defaultParentCategoryName);

  const toggleActiveSelect = () => setActiveSelect(!activeSelect);

  const clearErrors = () => {
    setNameError(false);
  };

  const handleClose = () => {
    setCategoryData(defaultCategoryData);
    setActiveSelect(false);
    setSelectedParentCategoryName(defaultParentCategoryName);
    clearErrors();
    onClickClose();
  };

  const handleSuccess = () => {
    setButtonDisabled(false);
    showSuccessToast(
      `The ${categoryData.parentCategoryId ? 'subcategory' : 'category'} "${
        categoryData.name
      }" has successfully been created.`
    );
    handleClose();
    getAllCategories();
  };

  const handleFailure = (error) => {
    setButtonDisabled(false);
    showErrorToast(
      `The ${categoryData.parentCategoryId ? 'subcategory' : 'category'} "${
        categoryData.name
      }" failed to be created due to error: ${error}.`
    );
    handleClose();
  };

  const handleCategoryNameChange = (e) => {
    setCategoryData({
      ...categoryData,
      name: e.target.value,
    });
    clearErrors();
  };

  const handleParentCategoryChange = (id, name) => {
    setCategoryData({
      ...categoryData,
      parentCategoryId: id,
    });
    setSelectedParentCategoryName(name);
    setActiveSelect(false);
    clearErrors();
  };

  const validateForm = () => {
    const existingSubCategoryNames = categories.filter(
      (c) =>
        c.parentCategoryId === categoryData.parentCategoryId &&
        c.name.toUpperCase().trim() === categoryData.name.toUpperCase().trim()
    );

    let isValid = true;
    if (existingSubCategoryNames.length) {
      setNameError(true);
      isValid = false;
    } else {
      setNameError(false);
    }
    return isValid;
  };

  const submitForm = () => {
    setButtonDisabled(true);
    const res = createCategory({
      name: categoryData.name.trim(),
      parentCategoryId: categoryData.parentCategoryId,
    });
    res
      .then(() => {
        handleSuccess();
      })
      .catch((e) => handleFailure(e));
  };

  const handleSubmit = () => {
    if (validateForm()) {
      submitForm();
    }
  };

  const renderContent = () => {
    const isSubmitDisabled =
      buttonDisabled ||
      !categoryData.name ||
      categoryData.parentCategoryId === undefined;

    return (
      <div className="category-modal content">
        <h1>New Category</h1>
        <h2>Create a new category or subcategory.</h2>
        <div className="category-form-container">
          <div
            className={`modal-section modal-row ${
              nameError ? 'name-error' : 'hide-name-error'
            }`}
          >
            <label>Name of Category</label>
            <input
              className="modal-elem"
              name="name"
              value={categoryData.name}
              onChange={handleCategoryNameChange}
              type="text"
              required
            />
            <span>This category name already exists.</span>
          </div>
          <div className="modal-section select">
            <label>Parent Category</label>
            <div className={`modal-elem ${activeSelect ? 'up' : 'down'}`}>
              <button type="button" onClick={toggleActiveSelect}>
                {selectedParentCategoryName}
              </button>
            </div>
            <ul
              className={`${activeSelect ? 'active' : 'inactive'}`}
              onMouseLeave={() => setActiveSelect(false)}
            >
              <li>
                <button
                  type="button"
                  onClick={() =>
                    handleParentCategoryChange(null, 'Main Category')
                  }
                >
                  Main Category
                </button>
              </li>
              {categories
                .filter((c) => !c.parentCategoryId)
                .map((c) => {
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => handleParentCategoryChange(c.id, c.name)}
                      >
                        {' '}
                        {c.name}{' '}
                      </button>
                    </li>
                  );
                })}
            </ul>
          </div>
          <div className="button-container">
            <Button
              variation="primary-outline"
              width="140px"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variation="black"
              width="140px"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      className="category-modal"
      isOpen={isOpen}
      onRequestClose={handleClose}
    >
      <div className="container">
        <div className="close-button">
          <CloseButton onClick={handleClose} />
        </div>
        {renderContent()}
      </div>
    </Modal>
  );
};

CategoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      parentCategoryId: PropTypes.number,
    })
  ).isRequired,
  onClickClose: PropTypes.func.isRequired,
  getAllCategories: PropTypes.func.isRequired,
};

export default CategoryModal;
